/**
 * Cart-total money display shared by every family cart surface (day-view
 * footer, week right-rail panel, mobile bar). When the server preview
 * says a subsidy applies, the gross total renders struck through beside
 * the net ("~~$3.95~~ $0.00") — the same treatment the item rows and
 * checkout lines use — so "what you'll pay" and "what it's worth" read
 * consistently everywhere. Without a subsidy (or while the preview is
 * loading) it renders the single plain amount, exactly as before.
 */

import type { ReactElement } from 'react'

import { previewPayableCents, type PreviewTotals } from '@/lib/family-orders/price-preview'
import { formatCents } from '@/lib/school-orders/money'

export function CartTotalPrice({
  totals,
  fallbackCents,
  currency,
  className,
}: {
  /** Server-computed cart totals; null while loading / cart empty. */
  totals: PreviewTotals | null
  /** Gross subtotal from the cart store — shown until totals load. */
  fallbackCents: number | null
  currency: string
  /** Styling for the (net) amount — each surface passes its own scale. */
  className: string
}): ReactElement {
  // With totals loaded, the single amount is the PAYABLE total (net of
  // subsidies, inclusive of tax) — it must equal what submit charges.
  if (!totals || totals.subsidyCents <= 0) {
    const cents = totals ? previewPayableCents(totals) : fallbackCents
    return <span className={className}>{cents !== null ? formatCents(cents, currency) : '—'}</span>
  }
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <s className="text-sm font-normal tabular-nums text-muted-foreground/70">
        {formatCents(totals.grossCents, currency)}
      </s>
      <span className={className}>{formatCents(previewPayableCents(totals), currency)}</span>
    </span>
  )
}
